import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import {
  EmailSchedule,
  EmailScheduleCreateRequest,
  EmailScheduleUpdateRequest,
  EmailScheduleListResponse,
  EmailScheduleResponse,
  EmailScheduleDeleteResponse
} from '../models/emailSchedule.model';

@Injectable({
  providedIn: 'root',
})
export class EmailScheduleService {
  private _allSchedules: BehaviorSubject<EmailScheduleListResponse | null> = new BehaviorSubject(null);

  /**
   * Constructor
   */
  constructor(private _httpClient: HttpClient) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get allSchedules$(): Observable<EmailScheduleListResponse> {
    return this._allSchedules.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all email schedules with pagination and filtering
   */
  getAll(
    page: number = 1,
    limit: number = 10,
    isActive?: boolean,
    templateId?: string
  ): Observable<EmailScheduleListResponse> {
    const params: any = {
      page,
      limit,
    };

    if (isActive !== undefined && isActive !== null) {
      params.isActive = isActive;
    }

    if (templateId) {
      params.templateId = templateId;
    }

    return this._httpClient
      .get<EmailScheduleListResponse>(environment.apiURL + 'admin/email-schedules', { params })
      .pipe(
        tap((response) => {
          this._allSchedules.next(response);
        })
      );
  }

  /**
   * Get single email schedule by ID
   */
  getById(id: string): Observable<EmailScheduleResponse> {
    return this._httpClient.get<EmailScheduleResponse>(
      environment.apiURL + 'admin/email-schedules/' + id
    );
  }

  /**
   * Create new email schedule
   */
  create(data: EmailScheduleCreateRequest): Observable<EmailScheduleResponse> {
    return this.allSchedules$.pipe(
      take(1),
      switchMap((schedules) =>
        this._httpClient
          .post<EmailScheduleResponse>(environment.apiURL + 'admin/email-schedules', data)
          .pipe(
            map((response) => {
              if (schedules && schedules.schedules) {
                schedules.schedules.unshift(response.schedule);
                schedules.total += 1;
                this._allSchedules.next(schedules);
              }
              return response;
            })
          )
      )
    );
  }

  /**
   * Update email schedule
   */
  update(id: string, data: EmailScheduleUpdateRequest): Observable<EmailScheduleResponse> {
    return this.allSchedules$.pipe(
      take(1),
      switchMap((schedules) =>
        this._httpClient
          .put<EmailScheduleResponse>(environment.apiURL + 'admin/email-schedules/' + id, data)
          .pipe(
            map((response) => {
              if (schedules && schedules.schedules) {
                const index = schedules.schedules.findIndex((item) => item._id === id);
                if (index !== -1) {
                  schedules.schedules[index] = response.schedule;
                  this._allSchedules.next(schedules);
                }
              }
              return response;
            })
          )
      )
    );
  }

  /**
   * Delete email schedule
   */
  delete(id: string): Observable<EmailScheduleDeleteResponse> {
    return this.allSchedules$.pipe(
      take(1),
      switchMap((schedules) =>
        this._httpClient
          .delete<EmailScheduleDeleteResponse>(environment.apiURL + 'admin/email-schedules/' + id)
          .pipe(
            map((response) => {
              if (schedules && schedules.schedules) {
                const index = schedules.schedules.findIndex((item) => item._id === id);
                if (index !== -1) {
                  schedules.schedules.splice(index, 1);
                  schedules.total -= 1;
                  this._allSchedules.next(schedules);
                }
              }
              return response;
            })
          )
      )
    );
  }

  /**
   * Activate email schedule
   */
  activate(id: string): Observable<EmailScheduleResponse> {
    return this.allSchedules$.pipe(
      take(1),
      switchMap((schedules) =>
        this._httpClient
          .post<EmailScheduleResponse>(environment.apiURL + 'admin/email-schedules/' + id + '/activate', {})
          .pipe(
            map((response) => {
              if (schedules && schedules.schedules) {
                const index = schedules.schedules.findIndex((item) => item._id === id);
                if (index !== -1) {
                  schedules.schedules[index] = response.schedule;
                  this._allSchedules.next(schedules);
                }
              }
              return response;
            })
          )
      )
    );
  }

  /**
   * Deactivate email schedule
   */
  deactivate(id: string): Observable<EmailScheduleResponse> {
    return this.allSchedules$.pipe(
      take(1),
      switchMap((schedules) =>
        this._httpClient
          .post<EmailScheduleResponse>(environment.apiURL + 'admin/email-schedules/' + id + '/deactivate', {})
          .pipe(
            map((response) => {
              if (schedules && schedules.schedules) {
                const index = schedules.schedules.findIndex((item) => item._id === id);
                if (index !== -1) {
                  schedules.schedules[index] = response.schedule;
                  this._allSchedules.next(schedules);
                }
              }
              return response;
            })
          )
      )
    );
  }
}
